import * as t from "./actionTypes";

import { alertService, listCategoryService } from "client-app-core";
import { unsubscribe } from "../contextStreaming";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import each from "lodash/each";
import size from "lodash/size";
import keys from "lodash/keys";
export {
	startLiveCameraStream,
	startCamerasLinkedItemsStream,
	startListStream,
	startActivityStream,
	startGateRunnerActivityStream,
	startEventActivityStream,
	startAttachmentStream,
	startEventPinnedItemsStream,
	subscriptionReceived,
	startFOVItemStream,
	startCamerasInRangeStream,
	startDevicesInRangeStream,
	startCameraInRangeVideoStream,
	startEventCameraStream,
	startFloorPlanCameraStream,
	startFloorPlanDevicesStream,
	startTrackHistoryStream,
	startRulesStream,
	startProximityEntitiesStream,
	startEventPatrolStream,
	unsubscribe,
	simpleUnsub
} from "../contextStreaming";

/* Add the entity context to the state
 * @param entity: entity object to be set on context
 */
export const addContext = (id, entity) => {
	return {
		type: t.ADD_CONTEXT,
		payload: {
			id,
			entity
		}
	};
};

/* Update context in state (ie if a tracks course)
 * @param contextId: entity that owns that subscription
 * @param update: updated entity object
 */
export const updateContext = (contextId, update) => {
	return {
		type: t.UPDATE_CONTEXT,
		payload: {
			contextId,
			update
		}
	};
};

/* Update property on context in state (ie camerasInRange on entity)
 * @param contextId: entity that owns that subscription
 * @param updateProp: updated entity property
 * @param updateVal: updated entity property value
 */
export const updateContextProperty = (contextId, updateProp, updateVal) => {
	return {
		type: t.UPDATE_CONTEXT_PROPERTY,
		payload: {
			contextId,
			updateProp,
			updateVal
		}
	};
};

const OCCURRENCE_PAGE_SIZE = 50;

export const fetchOccurrencesPage = ({ alertId, sort = "desc", reset = false }) => {
	return (dispatch, getState) => {
		const pag = getState().contextualData?.[alertId]?.occurrencePagination || {};
		if (pag.loading) return;
		if (!reset && !pag.hasMore) return;

		const snapshot = reset ? new Date().toISOString() : pag.snapshot || new Date().toISOString();
		const offset = reset ? 0 : pag.offset || 0;

		dispatch({
			type: t.ALERT_OCCURRENCES_PAGE_REQUESTED,
			payload: { contextId: alertId, reset, sort, snapshot }
		});
		alertService.getAlertOccurrences(
			alertId,
			{ size: OCCURRENCE_PAGE_SIZE, offset, sort, snapshot },
			(err, response) => {
				if (err) {
					dispatch({ type: t.ALERT_OCCURRENCES_PAGE_FAILED, payload: { contextId: alertId } });
					return;
				}
				dispatch({
					type: t.ALERT_OCCURRENCES_PAGE_RECEIVED,
					payload: {
						contextId: alertId,
						sort,
						reset,
						snapshot,
						items: response.items || [],
						hasMore: response.hasMore ?? false,
						offset: response.offset ?? offset
					}
				});
			}
		);
	};
};

export const clearAlertOccurrences = alertId => ({
	type: t.CLEAR_ALERT_OCCURRENCES,
	payload: { contextId: alertId }
});

export const startOccurrenceStream = (alertId, subscriberRef, onReady) => {
	return dispatch => {
		alertService
			.streamAlertOccurrences(alertId, (err, response) => {
				if (err) return;
				if (response.type === "occurrence-batch") {
					dispatch({
						type: t.ALERT_OCCURRENCE_BATCH_RECEIVED,
						payload: { contextId: alertId, changes: response.changes }
					});
				}
			})
			.then(subscription => {
				dispatch({
					type: t.SET_SUBSCRIPTION,
					payload: {
						contextId: alertId,
						subscription: subscription.channel,
						subscriptionId: "occurrences",
						subscriberRef
					}
				});
				onReady?.();
			});
	};
};

const updateContextListCategories = (contextId, categories) => {
	return {
		type: t.UPDATE_CONTEXT_LIST_CATEGORIES,
		payload: {
			contextId,
			categories
		}
	};
};
export const getContextListCategory = (categoryId, contextId) => {
	return dispatch => {
		listCategoryService.getListCategory(categoryId, (err, response) => {
			if (err) {
				console.log(err);
			}
			if (!response) {
				return;
			}
			dispatch(updateContextListCategories(contextId, [response]));
		});
	};
};

/*
 * Completely remove a context object
 * @param contextId: ID of entity used as context
 */
export const removeContext = contextId => {
	return {
		type: t.REMOVE_CONTEXT,
		payload: {
			contextId
		}
	};
};

/*
 * Completely remove a subscription and related data
 * @param contextId: ID of entity used as context
 * @param subscriptionId: ID of feed and related subscription
 */
export const removeFeed = (contextId, subscriptionId) => {
	return {
		type: t.REMOVE_SUBSCRIPTION,
		payload: {
			contextId,
			subscriptionId
		}
	};
};

/*
 * Remove a subscribed component from the subscribers array
 * @param contextId: ID of entity used as context
 * @param subscriptionId: ID of feed and related subscription
 * @param subscriberRef: Reference to component accessing a feed
 */
export const removeSubscriber = (contextId, subscriptionId, subscriberRef) => {
	return {
		type: t.REMOVE_SUBSCRIBER,
		payload: {
			contextId,
			subscriptionId,
			subscriberRef
		}
	};
};

/*
 * Add a subscribed component to the subscribers array
 * @param contextId: ID of entity used as context
 * @param subscriptionId: ID of feed and related subscription
 * @param subscriberRef: Reference to component accessing a feed
 */
export const addSubscriber = (contextId, subscriptionId, subscriberRef) => {
	return {
		type: t.ADD_SUBSCRIBER,
		payload: {
			contextId,
			subscriptionId,
			subscriberRef
		}
	};
};

/*
 * Check whether a feed should be unsubscribed from
 * @param subscriptions: A context object's subscriptions object
 * @param subscriptionId: ID of feed and related subscription
 */
const shouldUnsubscribeFeed = (subscriptions, subscriptionId, subscriberRef) => {
	return dispatch => {
		const subscription = subscriptions[subscriptionId];

		// Error catch in the case of multiple unsubscribe calls
		if (!subscription) {
			return false;
			// Unsubscribe if this is the last subscriber
		} else if (
			subscription.subscribers.length === 0 ||
			(subscription.subscribers.length === 1 && includes(subscription.subscribers, subscriberRef))
		) {
			if (subscription.subscription) {
				// If there is a valid subscription
				const channel = subscription.subscription;

				// Iterate over subscriptions involving multiple channels
				isArray(channel)
					? each(channel, channel => dispatch(unsubscribe(channel)))
					: dispatch(unsubscribe(channel));
			}
			return true;
		} else {
			return false;
		}
	};
};

/*
 * May need to pass subscriptions from passed props
 * @param contextId: ID of entity used as context
 * @param subscriptionId: ID of feed and related subscription
 * @param subscriberRef: Reference to component accessing a feed
 */
export const unsubscribeFromFeed = (contextId, subscriptionId, subscriberRef, forReplay) => {
	return (dispatch, getState) => {
		// Exit if context doesn't exist
		if (!getState().contextualData[contextId]) return;

		const context = getState().contextualData[contextId];
		const subscriptions = context.subscriptions;
		if (!(context.trackHistory && subscriptionId === "entity")) {
			// Check if this is the last subscriber
			if (dispatch(shouldUnsubscribeFeed(subscriptions, subscriptionId, subscriberRef))) {
				// Remove feed from context
				dispatch(removeFeed(contextId, subscriptionId));
				// Check if this is the last subscription to context
				if (size(subscriptions) <= 1 && !forReplay) {
					// Remove context entirely
					dispatch(removeContext(contextId));
				}
			} else {
				// Remove subscriber from subscriber array
				dispatch(removeSubscriber(contextId, subscriptionId, subscriberRef));
			}
		}
	};
};

export const unsubscribeFromContext = (contextId, subscriberRef, forReplay = false) => {
	return (dispatch, getState) => {
		if (!getState().contextualData[contextId]) return;
		const context = getState().contextualData[contextId];
		const { subscriptions, entity } = context;
		if (subscriptions) {
			if (!entity) return;
			const { id } = entity;
			Object.keys(subscriptions).forEach(subscription =>
				dispatch(unsubscribeFromFeed(id, subscription, subscriberRef), forReplay)
			);
		}
	};
};

// Clear all contexts that are only being subscribed to by profile on close
export const removeProfileSubscription = () => {
	return (dispatch, getState) => {
		const { contextualData, appState } = getState();
		const { selectedContext } = appState.contextPanel.contextPanelData;
		each(contextualData, (context, contextId) => {
			if (!context.trackHistory && Object.values(selectedContext).includes(contextId)) {
				const subscriptionIds = keys(context.subscriptions);
				each(subscriptionIds, subscriptionId =>
					dispatch(unsubscribeFromFeed(contextId, subscriptionId, "profile"))
				);
			}
		});
	};
};

export const unsubscribeEntityStreamSubscription = entityId => {
	return (dispatch, getState) => {
		const contextualData = getState().contextualData;
		const subscription = contextualData?.[entityId]?.subscriptions?.entity?.subscription;
		if (Array.isArray(subscription)) {
			subscription.forEach(value => {
				dispatch(unsubscribeFromFeed(entityId, value, "profile"));
			});
		} else {
			dispatch(unsubscribeFromFeed(entityId, subscription, "profile"));
		}
	};
};

export const updateContextPropertyWithDotNotation = (contextId, updateProp, updateVal) => {
	return {
		type: t.UPDATE_CONTEXT_PROPERTY_WITH_DOT_NOTATION,
		payload: {
			contextId,
			updateProp,
			updateVal
		}
	};
};
